import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Chip,
  IconButton,
  Divider,
  Alert,
  Stack,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  LinearProgress,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Quiz as QuizIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import ExamQuestionPayloadEditor from './ExamQuestionPayloadEditor';

const QUESTION_TYPES = [
  { value: 'true_false', label: 'True / False' },
  { value: 'yes_no', label: 'Yes / No' },
  { value: 'single_choice', label: 'Single choice (objective)' },
  { value: 'multiple_choice', label: 'Multiple choice' },
  { value: 'short_text', label: 'Short text answer' },
  { value: 'match', label: 'Match the following' }
];

const defaultPayload = (type) => {
  switch (type) {
    case 'true_false':
      return { correct: true };
    case 'yes_no':
      return { correct: 'yes' };
    case 'single_choice':
      return {
        options: [
          { key: 'a', text: 'Option A' },
          { key: 'b', text: 'Option B' },
          { key: 'c', text: 'Option C' },
          { key: 'd', text: 'Option D' }
        ],
        correct: 'a'
      };
    case 'multiple_choice':
      return {
        options: [
          { key: 'a', text: 'Option A' },
          { key: 'b', text: 'Option B' }
        ],
        correct: ['a']
      };
    case 'short_text':
      return { expected: '', case_insensitive: true };
    case 'match':
      return {
        left: ['Item 1', 'Item 2'],
        right: ['Match A', 'Match B'],
        pairs: [
          [0, 0],
          [1, 1]
        ]
      };
    default:
      return {};
  }
};

/** Normalize axios API body: { success, data } or legacy shape */
const unwrapExamPayload = (responseData) => {
  if (!responseData || typeof responseData !== 'object') return null;
  if (responseData.data != null && typeof responseData.data === 'object' && responseData.data.id) {
    return responseData.data;
  }
  if (responseData.id) return responseData;
  return null;
};

const renderPreviewQuestionBody = (q) => {
  const pl = q.payload && typeof q.payload === 'object' ? q.payload : {};
  switch (q.question_type) {
    case 'true_false':
      return (
        <RadioGroup row value={pl.correct === true ? 'true' : 'false'}>
          <FormControlLabel value="true" control={<Radio />} label="True" disabled />
          <FormControlLabel value="false" control={<Radio />} label="False" disabled />
        </RadioGroup>
      );
    case 'yes_no':
      return (
        <RadioGroup row value={pl.correct === 'no' ? 'no' : 'yes'}>
          <FormControlLabel value="yes" control={<Radio />} label="Yes" disabled />
          <FormControlLabel value="no" control={<Radio />} label="No" disabled />
        </RadioGroup>
      );
    case 'single_choice':
      return (
        <RadioGroup value={pl.correct || ''}>
          {(pl.options || []).map((o) => (
            <FormControlLabel key={o.key} value={o.key} control={<Radio />} label={o.text || o.key} disabled />
          ))}
        </RadioGroup>
      );
    case 'multiple_choice':
      return (
        <Stack spacing={0.5}>
          {(pl.options || []).map((o) => (
            <FormControlLabel
              key={o.key}
              control={<Checkbox checked={Array.isArray(pl.correct) && pl.correct.includes(o.key)} disabled />}
              label={o.text || o.key}
            />
          ))}
        </Stack>
      );
    case 'short_text':
      return (
        <Typography variant="body2" color="text.secondary">
          Expected answer: {pl.expected != null && pl.expected !== '' ? String(pl.expected) : '(any / manual grading)'}
          {pl.case_insensitive ? ' · case-insensitive match' : ''}
        </Typography>
      );
    case 'match':
      return (
        <Typography variant="body2" color="text.secondary" component="div">
          {(pl.left || []).map((left, i) => (
            <div key={i}>
              {left} → {(pl.right || [])[pl.pairs?.[i]?.[1] ?? i] || '—'}
            </div>
          ))}
        </Typography>
      );
    default:
      return (
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
          {JSON.stringify(pl)}
        </Typography>
      );
  }
};

const TrainingExamAdmin = ({ categories }) => {
  const navigate = useNavigate();
  const standaloneCategoryId = useMemo(
    () => categories?.find((c) => c.is_system_category || c.category_code === 'SYS_STANDALONE_EXAMS')?.id,
    [categories]
  );
  const [categoryId, setCategoryId] = useState('');
  const [spots, setSpots] = useState([]);
  const [titles, setTitles] = useState([]);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewExam, setPreviewExam] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [examDialog, setExamDialog] = useState(false);
  const [examForm, setExamForm] = useState({
    name: '',
    description: '',
    learning_spot_id: '',
    training_title_id: '',
    status: 'draft',
    time_limit_minutes: '',
    pass_score_percent: '',
    questions_per_session: '',
    include_in_standalone_catalog: false
  });
  const [editingExamId, setEditingExamId] = useState(null);

  const [qDialog, setQDialog] = useState(false);
  const [qForm, setQForm] = useState({
    question_type: 'true_false',
    prompt: '',
    points: 1,
    payload: defaultPayload('true_false')
  });
  const [editingQId, setEditingQId] = useState(null);

  const loadSpots = useCallback(async () => {
    if (!categoryId) {
      setSpots([]);
      return;
    }
    try {
      const r = await api.get('/training/learning-spots', { params: { category_id: categoryId } });
      setSpots(r.data.data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load sub-categories');
    }
  }, [categoryId]);

  useEffect(() => {
    loadSpots();
  }, [loadSpots]);

  const loadTitles = async (spotId) => {
    if (!spotId) {
      setTitles([]);
      return;
    }
    try {
      const r = await api.get(`/training/learning-spots/${spotId}/titles`);
      setTitles(r.data.data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load courses');
    }
  };

  const loadExams = useCallback(async () => {
    if (!categoryId) {
      setExams([]);
      return;
    }
    setLoading(true);
    try {
      const r = await api.get('/training/exams', { params: { category_id: categoryId } });
      setExams(r.data.data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load exams');
    } finally {
      setLoading(false);
    }
  }, [categoryId]);

  useEffect(() => {
    loadExams();
  }, [loadExams]);

  const loadExamDetail = async (id) => {
    setLoadingDetail(true);
    try {
      const r = await api.get(`/training/exams/${id}`);
      const ex = unwrapExamPayload(r.data);
      if (!ex?.id) {
        toast.error('Invalid exam response from server');
        setSelectedExam(null);
        setQuestions([]);
        return;
      }
      setSelectedExam(ex);
      setQuestions(Array.isArray(ex.questions) ? ex.questions : []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load exam');
      setSelectedExam(null);
      setQuestions([]);
    } finally {
      setLoadingDetail(false);
    }
  };

  const openEditExam = (ex) => {
    setEditingExamId(ex.id);
    setExamForm({
      name: ex.name,
      description: ex.description || '',
      learning_spot_id: ex.learning_spot_id || '',
      training_title_id: ex.training_title_id || '',
      status: ex.status || 'draft',
      time_limit_minutes: ex.time_limit_minutes ?? '',
      pass_score_percent: ex.pass_score_percent ?? '',
      questions_per_session: ex.questions_per_session ?? '',
      include_in_standalone_catalog: !!ex.include_in_standalone_catalog
    });
    if (ex.learning_spot_id) loadTitles(ex.learning_spot_id);
    else setTitles([]);
    setExamDialog(true);
  };

  const openPreviewExam = async (ex) => {
    setPreviewOpen(true);
    setPreviewExam(null);
    setPreviewLoading(true);
    try {
      const r = await api.get(`/training/exams/${ex.id}`);
      const full = unwrapExamPayload(r.data);
      if (!full?.id) {
        toast.error('Could not load exam for preview');
        setPreviewOpen(false);
        return;
      }
      setPreviewExam(full);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load preview');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const openNewExam = () => {
    setEditingExamId(null);
    const preStandalone = !!(standaloneCategoryId && categoryId === standaloneCategoryId);
    setExamForm({
      name: '',
      description: '',
      learning_spot_id: '',
      training_title_id: '',
      status: 'draft',
      time_limit_minutes: '',
      pass_score_percent: '',
      questions_per_session: '',
      include_in_standalone_catalog: preStandalone
    });
    setTitles([]);
    setExamDialog(true);
  };

  const saveExam = async () => {
    if (!examForm.name?.trim()) {
      toast.error('Exam name is required');
      return;
    }
    if (!examForm.include_in_standalone_catalog && !categoryId) {
      toast.error('Select a category, or enable Standalone exam catalog');
      return;
    }
    if (
      !examForm.include_in_standalone_catalog &&
      standaloneCategoryId &&
      categoryId === standaloneCategoryId
    ) {
      toast.error('Enable “Standalone exam catalog” to add exams to the system catalog, or choose another category in the filter.');
      return;
    }
    try {
      const base = {
        name: examForm.name.trim(),
        description: examForm.description || null,
        status: examForm.status,
        time_limit_minutes: examForm.time_limit_minutes ? parseInt(examForm.time_limit_minutes, 10) : null,
        pass_score_percent: examForm.pass_score_percent !== '' ? parseFloat(examForm.pass_score_percent, 10) : null,
        questions_per_session:
          examForm.questions_per_session !== '' && examForm.questions_per_session != null
            ? parseInt(examForm.questions_per_session, 10)
            : null
      };
      let payload;
      if (examForm.include_in_standalone_catalog) {
        payload = {
          ...base,
          include_in_standalone_catalog: true
        };
      } else {
        payload = {
          ...base,
          include_in_standalone_catalog: false,
          category_id: categoryId,
          learning_spot_id: examForm.learning_spot_id || null,
          training_title_id: examForm.training_title_id || null
        };
      }
      if (editingExamId) {
        await api.put(`/training/exams/${editingExamId}`, payload);
        toast.success('Exam updated');
      } else {
        await api.post('/training/exams', payload);
        toast.success('Exam created');
      }
      setExamDialog(false);
      loadExams();
      if (editingExamId) loadExamDetail(editingExamId);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Save failed');
    }
  };

  const deleteExam = async (ex) => {
    const id = ex.id;
    const ls = ex.lifecycle_status || 'active';
    if (ls === 'archived') return;
    if (ls === 'active') {
      if (!window.confirm('Mark this exam inactive? It will be removed from course outlines. You can archive it later.')) return;
    } else if (!window.confirm('Archive this exam? It will be hidden from the exam list.')) return;
    try {
      const r = await api.delete(`/training/exams/${id}`);
      toast.success(r.data?.message || 'Updated');
      if (selectedExam?.id === id) {
        setSelectedExam(null);
        setQuestions([]);
      }
      loadExams();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Delete failed');
    }
  };

  const openNewQuestion = () => {
    if (!selectedExam) return;
    setEditingQId(null);
    const p = defaultPayload('true_false');
    setQForm({
      question_type: 'true_false',
      prompt: '',
      points: 1,
      payload: p
    });
    setQDialog(true);
  };

  const saveQuestion = async () => {
    if (!selectedExam || !qForm.prompt?.trim()) {
      toast.error('Prompt is required');
      return;
    }
    const parsedPayload = qForm.payload && typeof qForm.payload === 'object' ? qForm.payload : {};
    try {
      const body = {
        question_type: qForm.question_type,
        prompt: qForm.prompt.trim(),
        points: parseFloat(String(qForm.points)) || 1,
        payload: parsedPayload
      };
      if (editingQId) {
        await api.put(`/training/exams/${selectedExam.id}/questions/${editingQId}`, body);
        toast.success('Question updated');
      } else {
        await api.post(`/training/exams/${selectedExam.id}/questions`, body);
        toast.success('Question added');
      }
      setQDialog(false);
      loadExamDetail(selectedExam.id);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Save failed');
    }
  };

  const deleteQuestion = async (qid) => {
    if (!selectedExam || !window.confirm('Remove this question?')) return;
    try {
      await api.delete(`/training/exams/${selectedExam.id}/questions/${qid}`);
      toast.success('Removed');
      loadExamDetail(selectedExam.id);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <QuizIcon color="primary" />
        Exams
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Exams belong to a <strong>category</strong>. For <strong>course-based</strong> exams, set sub-category and course. For exams offered <strong>without training enrollment</strong>, check <strong>Standalone exam catalog</strong> in the exam dialog — the exam is placed in the system-managed Standalone exam catalog category and listed in Browse Catalog. Questions use the visual editor; JSON is stored automatically.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select label="Category" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              <MenuItem value="">Select…</MenuItem>
              {(categories || []).map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={8} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button variant="contained" startIcon={<AddIcon />} disabled={!categoryId} onClick={openNewExam}>
            Create exam
          </Button>
        </Grid>
      </Grid>

      {!categoryId && <Alert severity="warning">Select a category to manage exams.</Alert>}

      {categoryId && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            All exams (table matches the Materials tab pattern: one row per item, actions on the right)
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Exam</TableCell>
                  <TableCell>Sub-category</TableCell>
                  <TableCell>Course</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Lifecycle</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      Loading…
                    </TableCell>
                  </TableRow>
                )}
                {!loading &&
                  exams.map((ex) => (
                    <TableRow
                      key={ex.id}
                      hover
                      selected={selectedExam?.id === ex.id}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => loadExamDetail(ex.id)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {ex.name}
                        </Typography>
                      </TableCell>
                      <TableCell>{ex.learningSpot?.name || '—'}</TableCell>
                      <TableCell>{ex.course?.name || '—'}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                          <span>{ex.status || '—'}</span>
                          {ex.include_in_standalone_catalog && (
                            <Chip size="small" label="Standalone catalog" color="secondary" variant="outlined" />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>{ex.lifecycle_status || 'active'}</TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Learner-style preview (read-only)">
                          <IconButton size="small" onClick={() => openPreviewExam(ex)} aria-label="Preview exam">
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit name, scope, limits">
                          <span>
                            <IconButton
                              size="small"
                              disabled={ex.lifecycle_status === 'archived'}
                              onClick={() => openEditExam(ex)}
                              aria-label="Edit exam"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={ex.lifecycle_status === 'inactive' ? 'Archive exam' : 'Mark inactive'}>
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={ex.lifecycle_status === 'archived'}
                              onClick={() => deleteExam(ex)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                {!loading && exams.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      No exams yet for this category.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {selectedExam && (
            <Card variant="outlined" sx={{ mt: 2 }}>
              <CardContent>
                {loadingDetail && <LinearProgress sx={{ mb: 2 }} />}
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
                  spacing={2}
                  sx={{ mb: 2 }}
                >
                  <Box>
                    <Typography variant="h6">{selectedExam.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedExam.description || '—'}
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip size="small" label={selectedExam.category?.name || 'Category'} />
                      {selectedExam.learningSpot && (
                        <Chip size="small" variant="outlined" label={selectedExam.learningSpot.name} />
                      )}
                      {selectedExam.course && <Chip size="small" variant="outlined" label={selectedExam.course.name} />}
                      {selectedExam.time_limit_minutes != null && (
                        <Chip size="small" label={`${selectedExam.time_limit_minutes} min limit`} />
                      )}
                      {selectedExam.pass_score_percent != null && (
                        <Chip size="small" label={`Pass ${selectedExam.pass_score_percent}%`} />
                      )}
                      {selectedExam.questions_per_session != null && selectedExam.questions_per_session > 0 && (
                        <Chip size="small" color="secondary" variant="outlined" label={`${selectedExam.questions_per_session} Q per attempt`} />
                      )}
                    </Box>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button
                      variant="outlined"
                      onClick={() => navigate(`/training/exam/${selectedExam.id}/take`)}
                      disabled={selectedExam.status !== 'published' && selectedExam.status !== 'Published'}
                    >
                      Take (test)
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={() => openPreviewExam(selectedExam)}
                    >
                      Preview
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<EditIcon />}
                      disabled={selectedExam.lifecycle_status === 'archived'}
                      onClick={() => openEditExam(selectedExam)}
                    >
                      Edit exam
                    </Button>
                  </Stack>
                </Stack>

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Questions ({questions.length})
                </Typography>
                {!loadingDetail && (
                  <>
                    <Button variant="outlined" startIcon={<AddIcon />} sx={{ mb: 2 }} onClick={openNewQuestion}>
                      Add question
                    </Button>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Prompt</TableCell>
                            <TableCell align="right">Pts</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {questions.map((q, i) => (
                            <TableRow key={q.id}>
                              <TableCell>{i + 1}</TableCell>
                              <TableCell>{q.question_type}</TableCell>
                              <TableCell sx={{ maxWidth: 280 }}>{q.prompt}</TableCell>
                              <TableCell align="right">{q.points}</TableCell>
                              <TableCell align="right">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setEditingQId(q.id);
                                    const pl =
                                      q.payload && typeof q.payload === 'object' ? q.payload : defaultPayload(q.question_type);
                                setQForm({
                                  question_type: q.question_type,
                                  prompt: q.prompt,
                                  points: q.points,
                                  payload: pl
                                });
                                setQDialog(true);
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" color="error" onClick={() => deleteQuestion(q.id)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                          {questions.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={5}>
                                No questions yet — use Add question or Preview to verify content.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {!selectedExam && !loading && exams.length > 0 && (
            <Typography color="text.secondary" sx={{ mt: 2 }}>
              Select a row to load questions, or use Preview / Edit from the actions column.
            </Typography>
          )}
        </Box>
      )}

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle sx={{ background: (t) => alpha(t.palette.primary.main, 0.12), borderBottom: 1, borderColor: 'divider' }}>
          Preview — {previewExam?.name || 'Exam'}
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            background: (theme) =>
              `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${theme.palette.background.paper} 140px)`
          }}
        >
          {previewLoading && <LinearProgress sx={{ mb: 2 }} />}
          {!previewLoading && previewExam && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Read-only view for authors. Correct answers are shown for your review.
              </Typography>
              {(previewExam.questions || []).length === 0 && (
                <Typography color="text.secondary">No questions in this exam yet.</Typography>
              )}
              <Stack spacing={2}>
                {(previewExam.questions || []).map((q, i) => {
                  const hues = ['#6366f1', '#0ea5e9', '#10b981', '#d946ef'];
                  const c = hues[i % hues.length];
                  return (
                    <Card
                      key={q.id || i}
                      elevation={0}
                      sx={{
                        borderLeft: 5,
                        borderColor: c,
                        bgcolor: (t) => alpha(c, 0.08),
                        border: '1px solid',
                        borderRightColor: 'divider',
                        borderTopColor: 'divider',
                        borderBottomColor: 'divider'
                      }}
                    >
                      <CardContent>
                        <Typography variant="caption" sx={{ color: c, fontWeight: 600 }}>
                          Question {i + 1} · {q.question_type} · {q.points} pts
                        </Typography>
                        <Typography variant="body1" sx={{ my: 1, fontWeight: 500 }}>
                          {q.prompt}
                        </Typography>
                        <Box sx={{ bgcolor: (t) => alpha(t.palette.background.paper, 0.7), borderRadius: 1, p: 1.5 }}>
                          {renderPreviewQuestionBody(q)}
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={examDialog} onClose={() => setExamDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingExamId ? 'Edit exam' : 'Create exam'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Exam name"
            required
            value={examForm.name}
            onChange={(e) => setExamForm({ ...examForm, name: e.target.value })}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Description"
            multiline
            minRows={2}
            value={examForm.description}
            onChange={(e) => setExamForm({ ...examForm, description: e.target.value })}
          />
          <FormControlLabel
            sx={{ mt: 1, alignItems: 'flex-start' }}
            control={
              <Checkbox
                checked={!!examForm.include_in_standalone_catalog}
                onChange={(e) => {
                  const v = e.target.checked;
                  setExamForm((f) => ({
                    ...f,
                    include_in_standalone_catalog: v,
                    learning_spot_id: v ? '' : f.learning_spot_id,
                    training_title_id: v ? '' : f.training_title_id
                  }));
                  if (v) setTitles([]);
                }}
              />
            }
            label={
              <Box>
                <Typography variant="body2">Standalone exam catalog</Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Places the exam in the system-managed category (no sub-category or course). Shown in Browse Catalog and Training → Standalone exams.
                </Typography>
              </Box>
            }
          />
          <FormControl fullWidth margin="normal" disabled={!!examForm.include_in_standalone_catalog}>
            <InputLabel>Sub-category (optional)</InputLabel>
            <Select
              label="Sub-category (optional)"
              value={examForm.learning_spot_id}
              onChange={(e) => {
                const v = e.target.value;
                setExamForm({ ...examForm, learning_spot_id: v, training_title_id: '' });
                loadTitles(v);
              }}
            >
              <MenuItem value="">—</MenuItem>
              {spots.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal" disabled={!!examForm.include_in_standalone_catalog || !examForm.learning_spot_id}>
            <InputLabel>Course (optional)</InputLabel>
            <Select
              label="Course (optional)"
              value={examForm.training_title_id}
              onChange={(e) => setExamForm({ ...examForm, training_title_id: e.target.value })}
              disabled={!!examForm.include_in_standalone_catalog || !examForm.learning_spot_id}
            >
              <MenuItem value="">—</MenuItem>
              {titles.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={examForm.status} onChange={(e) => setExamForm({ ...examForm, status: e.target.value })}>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="published">Published</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            margin="normal"
            type="number"
            label="Time limit (minutes)"
            value={examForm.time_limit_minutes}
            onChange={(e) => setExamForm({ ...examForm, time_limit_minutes: e.target.value })}
          />
          <TextField
            fullWidth
            margin="normal"
            type="number"
            label="Pass score %"
            value={examForm.pass_score_percent}
            onChange={(e) => setExamForm({ ...examForm, pass_score_percent: e.target.value })}
          />
          <TextField
            fullWidth
            margin="normal"
            type="number"
            label="Questions per attempt"
            value={examForm.questions_per_session}
            onChange={(e) => setExamForm({ ...examForm, questions_per_session: e.target.value })}
            helperText="Leave empty to show all questions in order. Set a number to randomly pick that many questions each time."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExamDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveExam}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={qDialog} onClose={() => setQDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingQId ? 'Edit question' : 'Add question'}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Question type</InputLabel>
            <Select
              label="Question type"
              value={qForm.question_type}
              onChange={(e) => {
                const t = e.target.value;
                const p = defaultPayload(t);
                setQForm({ ...qForm, question_type: t, payload: p });
              }}
            >
              {QUESTION_TYPES.map((qt) => (
                <MenuItem key={qt.value} value={qt.value}>
                  {qt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            margin="normal"
            label="Prompt / question text"
            required
            multiline
            minRows={2}
            value={qForm.prompt}
            onChange={(e) => setQForm({ ...qForm, prompt: e.target.value })}
          />
          <TextField
            fullWidth
            margin="normal"
            type="number"
            label="Points"
            value={qForm.points}
            onChange={(e) => setQForm({ ...qForm, points: e.target.value })}
          />
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Answers &amp; scoring
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Use the controls below. The server stores this as JSON — a read-only copy is shown for debugging.
          </Typography>
          <ExamQuestionPayloadEditor
            questionType={qForm.question_type}
            payload={qForm.payload}
            onChange={(next) => setQForm({ ...qForm, payload: next })}
          />
          <Paper variant="outlined" sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover' }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Stored payload (read-only)
            </Typography>
            <Typography
              component="pre"
              sx={{ m: 0, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
            >
              {JSON.stringify(qForm.payload ?? {}, null, 2)}
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveQuestion}>
            Save question
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrainingExamAdmin;
